import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { FpsOpenChatInputBox } from '@atlassian/search-ai/fps-open-chat-input-box';
import type { IssueLinkedPRsEntry } from '@atlassian/search-client/hydration/pr-types';
import type { PastMeetingOccurrence } from '@atlassian/search-client/past-meeting-occurrences/types';

import { type ProductKeys } from '../../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import type { SearchResultSharedProps } from '../../../../common/types';
import { useAppContext } from '../../../../common/ui/app-context';
import { isLeapQueryEnabled } from '../../../../common/utils/is-leap-query-enabled';
import { ThirdPartyQuerySuggestionsContainer } from '../../../../common/utils/third-party-query-suggestions-container';
import { useAnalytics } from '../../../../controllers/analytics';
import { useAreLLMPoweredFeaturesEnabled } from '../../../../controllers/entitlement-checks';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useIsUserOAuthed } from '../../../../controllers/oauth';
import { useSearchPageProactiveAiCohort } from '../../../../controllers/proactive-ai-experiment';
import { useSharedIsSainAnswerVisible } from '../../../../controllers/smart-answers-intent-store';
import { useSearchActions } from '../../../../controllers/store/search';

import type { searchResult_AtlassianSearchPage$key } from './__generated__/searchResult_AtlassianSearchPage.graphql';
import { BulkConnectBanner } from './bulk-connect-banner';
import { LeapQueryBanner } from './leap-query-banner';
import { messages } from './messages';
import { SearchResultRenderer } from './search-result-renderer';
import type { JiraIssueStatusAndColorName } from './search-result-renderer/search-jira-issue';

// How many search results to show before the OAuth banner
export const OAUTH_BANNER_POSITION = 4;
const LEAP_QUERY_BANNER_POSITION = 5;
const THIRD_PARTY_QUERY_SUGGESTION_POSITION = 5;
const FPS_ROVO_CHAT_INPUT_POSITION = 3;

export type SearchResultProps = SearchResultSharedProps & {
	node: searchResult_AtlassianSearchPage$key;
	position: number;
	/**
	 * Total number of search results currently rendered in the list. Used to
	 * clamp the FPS Rovo chat input box to the last result when there are fewer
	 * results than its preferred fixed position, so it is still shown on short
	 * result lists.
	 */
	totalResults?: number;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	originalQuery: string | null | undefined;
	logError: (error: any) => Promise<void>;
	compassTeamName?: string | null; // Optional, used for Compass teams
	jiraIssueStatus?: JiraIssueStatusAndColorName | null; // Optional, used for Jira issues
	jiraLinkedPRs?: IssueLinkedPRsEntry; // Optional, used for Jira issue linked PRs
	pastMeetingOccurrences?: PastMeetingOccurrence[] | null; // Optional, used for MS Teams recurring meetings
	smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
	mainContentWidth?: number;
	/**
	 * True when this result is being rendered as a row inside the Jira table
	 * view. Set by SearchResultsListLiteRenderer ONLY when it renders that table
	 * (which requires lite mode to be enabled, a Jira product to be selected, and
	 * issue/project results to exist). It is never set on the "All" tab or by the
	 * default renderer.
	 *
	 * This single flag drives two table-view-specific behaviours: the leaf
	 * renderers render their compact table-row variant, and the interleaved UI
	 * (banners, query suggestions) is suppressed so it does not break the table
	 * layout.
	 */
	isJiraTableView?: boolean;
	renderConnectorPromo?: () => React.ReactElement;
};

export const SearchResult = ({
	node,
	position,
	totalResults,
	thirdPartyConfigs,
	availableProducts,
	cloudId,
	searchQuery,
	originalQuery,
	selectedProduct,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	appMode,
	logError,
	compassTeamName,
	jiraIssueStatus,
	jiraLinkedPRs,
	pastMeetingOccurrences,
	smartAnswersPortalRef,
	mainContentWidth,
	selectedSites,
	isJiraTableView,
	renderConnectorPromo,
}: SearchResultProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchResult_AtlassianSearchPage on SearchResult
			@argumentDefinitions(
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includeFastJiraHydration: { type: "Boolean", defaultValue: false }
				includeAssets: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includeRisk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includeCodeSearch: { type: "Boolean", defaultValue: false }
				includeReactionsAndCommentCount: { type: "Boolean", defaultValue: false }
				includeCaseEmbeddedComments: { type: "Boolean", defaultValue: false }
			) {
				id
				...searchResultRenderer_AtlassianSearchPage
					@arguments(
						includeJiraLiteModeFields: $includeJiraLiteModeFields
						includeFastJiraHydration: $includeFastJiraHydration
						includeAssets: $includeAssets
						includePassionfruitAsk: $includePassionfruitAsk
						includeRisk: $includeRisk
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeNavBoostScore: $includeNavBoostScore
						includeCodeSearch: $includeCodeSearch
						includeReactionsAndCommentCount: $includeReactionsAndCommentCount
						includeCaseEmbeddedComments: $includeCaseEmbeddedComments
					)
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();
	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();
	const isLLMEnabled = useAreLLMPoweredFeaturesEnabled();
	const [areThirdPartyQuerySuggestionsVisible, setAreThirdPartyQuerySuggestionsVisible] =
		useState(false);

	const isSainAnswerVisible = useSharedIsSainAnswerVisible();

	const proactiveAiCohort = useSearchPageProactiveAiCohort();
	const isInlineChatInputCohort = proactiveAiCohort === 'inline_chat_input_in_results';

	const isInlineChatInputBoxShown = isInlineChatInputCohort && !isSainAnswerVisible && isLLMEnabled;

	// The FPS Rovo chat input box is normally injected at a fixed position
	// (FPS_ROVO_CHAT_INPUT_POSITION). When the result list is shorter than that
	// position, no result has that index and the box would never render. Clamp
	// the injection point to the last available result so it is still shown on
	// short result lists. When totalResults is unknown, fall back to the fixed
	// position to preserve the original behaviour.
	const effectiveFpsChatInputPosition =
		totalResults != null && totalResults > 0
			? Math.min(FPS_ROVO_CHAT_INPUT_POSITION, totalResults - 1)
			: FPS_ROVO_CHAT_INPUT_POSITION;

	// UI injected between search results (banners, query suggestions, …). It would
	// break the Jira table layout, so it renders everywhere except the table view.
	const interleavedContent = !isJiraTableView && (
		<>
			{position === OAUTH_BANNER_POSITION &&
				(renderConnectorPromo ? (
					renderConnectorPromo()
				) : (
					<BulkConnectBanner
						thirdPartyConfigs={thirdPartyConfigs}
						selectedProduct={selectedProduct}
						availableProducts={availableProducts}
					/>
				))}
			{position === LEAP_QUERY_BANNER_POSITION &&
				isLeapQueryEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled) &&
				!areThirdPartyQuerySuggestionsVisible && (
					<LeapQueryBanner
						searchQuery={searchQuery}
						originalQuery={originalQuery}
						primaryProduct={primaryProduct}
						cloudId={cloudId}
						logError={logError}
					/>
				)}
			{position === THIRD_PARTY_QUERY_SUGGESTION_POSITION &&
				!selectedProduct &&
				!isInlineChatInputBoxShown && (
					<ThirdPartyQuerySuggestionsContainer
						thirdPartyConfigs={thirdPartyConfigs}
						useIsUserOAuthed={useIsUserOAuthed}
						useSearchActions={useSearchActions}
						useAnalytics={useAnalytics}
						currentQuery={searchQuery}
						onVisible={() => setAreThirdPartyQuerySuggestionsVisible(true)}
					/>
				)}
			{position === effectiveFpsChatInputPosition && isInlineChatInputBoxShown && (
				<ErrorBoundary section="FpsRovoChatInputBox" fallback={null}>
					<FpsOpenChatInputBox
						query={searchQuery}
						searchSessionId={searchSessionId}
						cloudId={cloudId}
						sourceProduct={primaryProduct}
						subproduct={primarySubProduct}
						availableProducts={availableProducts}
						isVisible
						title={formatMessage(messages.fpsInlineRovoChatTitle)}
					/>
				</ErrorBoundary>
			)}
		</>
	);

	return (
		<>
			{interleavedContent}
			<ErrorBoundary section="SearchPageResultRecord">
				<SearchResultRenderer
					key={data.id}
					node={data}
					position={position}
					cloudId={cloudId}
					searchQuery={searchQuery}
					searchSessionId={searchSessionId}
					appMode={appMode}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					selectedProduct={selectedProduct}
					thirdPartyConfigs={thirdPartyConfigs}
					compassTeamName={compassTeamName}
					jiraIssueStatus={jiraIssueStatus}
					jiraLinkedPRs={jiraLinkedPRs}
					pastMeetingOccurrences={pastMeetingOccurrences}
					smartAnswersPortalRef={smartAnswersPortalRef}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
					// Being in the Jira table view is what makes a leaf render its
					// lite (table-row) variant.
					isLiteMode={isJiraTableView}
				/>
			</ErrorBoundary>
		</>
	);
};
